/**
 * QEEGMetrics.ts
 * Phase 3 - Quantitative EEG (qEEG) Metrics Service
 *
 * S2 note: Primary product paths use `qeegTrendTransforms`, long-study
 * materialization (`/qeeg-trends`), and `/api/advanced-analysis`. This service
 * remains as a **local fallback** for windowed coherence when the server path
 * is unavailable (see AdvancedConnectivityPanel). Do not use outputs for
 * diagnostic claims (assistive IFU freeze).
 *
 * Implements advanced quantitative EEG analysis:
 * - Amplitude-Integrated EEG (aEEG) - Neonatal brain monitoring
 * - Suppression Ratio - ICU sedation depth
 * - Burst Suppression Ratio (BSR) - Anesthesia monitoring
 * - Asymmetry Index - Stroke/lesion detection
 * - Coherence Analysis - Functional connectivity
 *
 * @author Nervedx EEG System
 * @version 3.0.0
 * @date 2026-02-01
 */

import SpectralAnalysisService, { CLINICAL_BANDS } from "./SpectralAnalysis";

// ============================================================================
// Type Definitions
// ============================================================================

export interface AEEGResult {
  timestamp: number[]; // Time points (seconds)
  upperMargin: number[]; // Upper amplitude margin (μV, log scale)
  lowerMargin: number[]; // Lower amplitude margin (μV, log scale)
  bandwidth: number[]; // Bandwidth (upper - lower)
  discontinuityScore: number; // Discontinuity score (0-100%)
  sleepWakeCycling: boolean; // Presence of sleep-wake cycling
  classification:
    | "normal"
    | "moderately_abnormal"
    | "severely_abnormal"
    | "flat_trace";
}

export interface SuppressionRatioResult {
  suppressionRatio: number; // Percentage of time <5 μV (0-100%)
  suppressionEpochs: Array<{
    // Detected suppression epochs
    start: number; // Start time (seconds)
    end: number; // End time (seconds)
    duration: number; // Duration (seconds)
    meanAmplitude: number; // Mean amplitude during suppression (μV)
  }>;
  totalSuppressionTime: number; // Total suppression time (seconds)
  longestSuppression: number; // Longest single suppression (seconds)
}

export interface BurstSuppressionResult {
  bsr: number; // Burst suppression ratio (0-100%)
  burstCount: number; // Number of burst events
  suppressionCount: number; // Number of suppression events
  burstDuration: {
    // Burst statistics
    mean: number;
    median: number;
    std: number;
  };
  suppressionDuration: {
    // Suppression statistics
    mean: number;
    median: number;
    std: number;
  };
  interburstInterval: number; // Mean interburst interval (seconds)
}

export interface AsymmetryResult {
  deltaAsymmetry: number; // Delta band asymmetry (-100% to +100%)
  thetaAsymmetry: number; // Theta band asymmetry
  alphaAsymmetry: number; // Alpha band asymmetry
  betaAsymmetry: number; // Beta band asymmetry
  overallAsymmetry: number; // Overall broadband asymmetry
  leftHemispherePower: number; // Total power in left hemisphere
  rightHemispherePower: number; // Total power in right hemisphere
  bandAsymmetry: Map<string, number>; // Band-specific asymmetry values
  affectedHemisphere: "left" | "right" | "none"; // Hemisphere with lower power
  severity: "mild" | "moderate" | "severe" | "none"; // Asymmetry severity
}

export interface CoherenceResult {
  channelPair: [string, string]; // Channel pair
  frequency: number[]; // Frequency bins (Hz)
  coherence: number[]; // Coherence values (0-1)
  peakCoherence: number; // Peak coherence value
  peakFrequency: number; // Frequency of peak coherence (Hz)
  meanCoherence: {
    // Mean coherence per band
    delta: number;
    theta: number;
    alpha: number;
    beta: number;
    gamma: number;
  };
}

export interface QEEGParams {
  // aEEG parameters
  aEEGWindowSize: number; // Window size for aEEG (seconds)
  aEEGHopSize: number; // Hop size for aEEG (seconds)

  // Suppression parameters
  suppressionThreshold: number; // Amplitude threshold for suppression (μV)
  minSuppressionDuration: number; // Minimum suppression duration (seconds)

  // Burst suppression parameters
  burstThreshold: number; // Amplitude threshold for burst (μV)
  minBurstDuration: number; // Minimum burst duration (seconds)

  // Asymmetry parameters
  asymmetrySignificanceThreshold: number; // Threshold for significant asymmetry (%)

  // Coherence parameters
  coherenceWindow: number; // Window size for coherence (seconds)
  coherenceOverlap: number; // Overlap percentage (0-100%)
}

// ============================================================================
// qEEG Metrics Service
// ============================================================================

export class QEEGMetricsService {
  // --------------------------------------------------------------------------
  // Comprehensive qEEG Metrics Computation
  // --------------------------------------------------------------------------

  /**
   * Compute all quantitative EEG metrics
   * One-stop method for comprehensive qEEG analysis
   *
   * @param signals Multi-channel EEG signals (μV)
   * @param channelNames Channel names (for asymmetry analysis)
   * @param samplingRate Sampling rate (Hz)
   * @param params qEEG parameters
   * @returns Comprehensive qEEG metrics
   */
  computeAllMetrics(
    signals: number[][],
    channelNames: string[],
    samplingRate: number,
    params: Partial<QEEGParams> = {},
  ): {
    aEEG: AEEGResult;
    suppressionRatio: SuppressionRatioResult;
    burstSuppression: BurstSuppressionResult;
    asymmetry: AsymmetryResult | null;
    spectralEdgeFrequency: number;
    medianFrequency: number;
    spectralEntropy: number;
  } {
    const fullParams = { ...QEEGMetricsService.getDefaultParams(), ...params };

    // Use first channel for single-channel metrics
    const primarySignal = signals[0];

    // Compute aEEG
    const aEEG = this.computeAEEG(primarySignal, samplingRate, fullParams);

    // Compute suppression ratio
    const suppressionRatio = this.computeSuppressionRatio(
      primarySignal,
      samplingRate,
      fullParams,
    );

    // Compute burst suppression ratio
    const burstSuppression = this.computeBurstSuppressionRatio(
      primarySignal,
      samplingRate,
      fullParams,
    );

    // Compute asymmetry index (requires left/right hemisphere channels)
    let asymmetry: AsymmetryResult | null = null;
    const leftChannels = signals.filter((_, idx) =>
      ["F3", "C3", "P3", "O1", "F7", "T3", "T5"].some((ch) =>
        channelNames[idx]?.includes(ch),
      ),
    );
    const rightChannels = signals.filter((_, idx) =>
      ["F4", "C4", "P4", "O2", "F8", "T4", "T6"].some((ch) =>
        channelNames[idx]?.includes(ch),
      ),
    );

    // Average the signals for each hemisphere if multiple channels are present
    const averageSignal = (channels: number[][]): number[] => {
      if (channels.length === 0) return [];
      const length = channels[0].length;
      const avg: number[] = [];
      for (let i = 0; i < length; i++) {
        let sum = 0;
        for (let j = 0; j < channels.length; j++) {
          sum += channels[j][i];
        }
        avg.push(sum / channels.length);
      }
      return avg;
    };

    if (leftChannels.length > 0 && rightChannels.length > 0) {
      const leftAvg = averageSignal(leftChannels);
      const rightAvg = averageSignal(rightChannels);
      asymmetry = this.computeAsymmetryIndex(
        leftAvg,
        rightAvg,
        samplingRate,
        fullParams,
      );
    }

    // Compute spectral metrics
    const spectralEdgeFrequency = this.computeSpectralEdgeFrequency(
      primarySignal,
      samplingRate,
      95,
    );
    const medianFrequency = this.computeMedianFrequency(
      primarySignal,
      samplingRate,
    );
    const spectralEntropy = this.computeSpectralEntropy(
      primarySignal,
      samplingRate,
    );

    return {
      aEEG,
      suppressionRatio,
      burstSuppression,
      asymmetry,
      spectralEdgeFrequency,
      medianFrequency,
      spectralEntropy,
    };
  }

  // --------------------------------------------------------------------------
  // Amplitude-Integrated EEG (aEEG)
  // --------------------------------------------------------------------------

  /**
   * Compute amplitude-integrated EEG (aEEG)
   * Used for neonatal brain monitoring and HIE prognosis
   * @param signal Raw EEG signal (μV)
   * @param samplingRate Sampling rate (Hz)
   * @param params qEEG parameters
   * @returns aEEG time-compressed amplitude display
   */
  computeAEEG(
    signal: number[],
    samplingRate: number,
    params: Partial<QEEGParams> = {},
  ): AEEGResult {
    if (!signal || signal.length === 0 || samplingRate <= 0) {
      return {
        timestamp: [],
        upperMargin: [],
        lowerMargin: [],
        bandwidth: [],
        discontinuityScore: 0,
        sleepWakeCycling: false,
        classification: "flat_trace",
      };
    }

    const fullParams = { ...QEEGMetricsService.getDefaultParams(), ...params };

    const configuredWindowSize = Math.floor(
      fullParams.aEEGWindowSize * samplingRate,
    );
    const windowSize = Math.min(signal.length, configuredWindowSize);
    const hopSize = Math.max(1, Math.floor(fullParams.aEEGHopSize * samplingRate));

    const timestamps: number[] = [];
    const upperMargin: number[] = [];
    const lowerMargin: number[] = [];
    const bandwidth: number[] = [];

    // Slide window across signal
    for (let i = 0; i + windowSize <= signal.length; i += hopSize) {
      const window = signal.slice(i, i + windowSize);

      // Apply Butterworth filter (2-15 Hz for aEEG)
      // For simplicity, use raw signal (assume pre-filtered)

      // Compute amplitude envelope (Hilbert transform approximation)
      const envelope = window.map(Math.abs);

      // Compute percentiles (90th and 10th percentile for upper/lower margins)
      const sorted = [...envelope].sort((a, b) => a - b);
      const upper90 = sorted[Math.floor(sorted.length * 0.9)];
      const lower10 = sorted[Math.floor(sorted.length * 0.1)];

      // Convert to log scale (μV) - characteristic of aEEG display
      const upperLog = upper90 > 0 ? Math.log10(upper90) : 0;
      const lowerLog = lower10 > 0 ? Math.log10(lower10) : 0;

      timestamps.push(i / samplingRate);
      upperMargin.push(upperLog);
      lowerMargin.push(lowerLog);
      bandwidth.push(upperLog - lowerLog);
    }

    // Compute discontinuity score (periods with low bandwidth)
    const discontinuousPoints = bandwidth.filter((bw) => bw < 0.5).length;
    const discontinuityScore =
      bandwidth.length > 0 ? (discontinuousPoints / bandwidth.length) * 100 : 0;

    // Detect sleep-wake cycling (periodic variations in bandwidth)
    const sleepWakeCycling = this.detectSleepWakeCycling(bandwidth);

    // Classify aEEG pattern
    const classification = this.classifyAEEG(
      upperMargin,
      lowerMargin,
      bandwidth,
      discontinuityScore,
    );

    return {
      timestamp: timestamps,
      upperMargin,
      lowerMargin,
      bandwidth,
      discontinuityScore,
      sleepWakeCycling,
      classification,
    };
  }

  private detectSleepWakeCycling(bandwidth: number[]): boolean {
    // Simple detection: Look for periodic variations (>20% amplitude change)
    // Real implementation would use spectral analysis of bandwidth signal
    if (bandwidth.length < 10) return false;

    let variations = 0;
    for (let i = 1; i < bandwidth.length; i++) {
      const change =
        Math.abs(bandwidth[i] - bandwidth[i - 1]) / (bandwidth[i - 1] + 1e-10);
      if (change > 0.2) variations++;
    }

    return variations > bandwidth.length * 0.3; // >30% points show variation
  }

  private classifyAEEG(
    upper: number[],
    lower: number[],
    bandwidth: number[],
    discontinuity: number,
  ): "normal" | "moderately_abnormal" | "severely_abnormal" | "flat_trace" {
    if (!upper.length || !lower.length || !bandwidth.length) {
      return "flat_trace";
    }

    const meanUpper = upper.reduce((a, b) => a + b, 0) / upper.length;
    const meanLower = lower.reduce((a, b) => a + b, 0) / lower.length;
    const meanBandwidth =
      bandwidth.reduce((a, b) => a + b, 0) / bandwidth.length;

    // Classification based on Hellström-Westas criteria (neonatal aEEG)
    if (meanUpper < 0.3 && meanLower < -0.5) {
      return "flat_trace"; // <2 μV upper margin
    } else if (meanUpper < 0.8 || discontinuity > 35 || meanBandwidth < 0.25) {
      return "severely_abnormal"; // Low voltage, highly discontinuous
    } else if (meanUpper < 1.2 || discontinuity > 15 || meanBandwidth < 0.45) {
      return "moderately_abnormal"; // Borderline voltages, some discontinuity
    } else {
      return "normal"; // Continuous normal voltage (>10 μV)
    }
  }

  // --------------------------------------------------------------------------
  // Suppression Ratio
  // --------------------------------------------------------------------------

  /**
   * Compute suppression ratio (percentage of time with amplitude <5 μV)
   * Used for ICU sedation monitoring
   * @param signal Raw EEG signal (μV)
   * @param samplingRate Sampling rate (Hz)
   * @param params qEEG parameters
   * @returns Suppression ratio and epochs
   */
  computeSuppressionRatio(
    signal: number[],
    samplingRate: number,
    params: Partial<QEEGParams> = {},
  ): SuppressionRatioResult {
    if (!signal || signal.length === 0 || samplingRate <= 0) {
      return {
        suppressionRatio: 0,
        suppressionEpochs: [],
        totalSuppressionTime: 0,
        longestSuppression: 0,
      };
    }

    const fullParams = { ...QEEGMetricsService.getDefaultParams(), ...params };

    const threshold = fullParams.suppressionThreshold;
    const minDuration = Math.floor(
      fullParams.minSuppressionDuration * samplingRate,
    );

    const suppressionEpochs: Array<{
      start: number;
      end: number;
      duration: number;
      meanAmplitude: number;
    }> = [];

    // Compute amplitude envelope (moving RMS)
    const windowSize = Math.floor(0.1 * samplingRate); // 100ms window
    const amplitude: number[] = [];

    for (let i = 0; i < signal.length; i += windowSize) {
      const window = signal.slice(i, i + windowSize);
      const rms = Math.sqrt(
        window.reduce((sum, val) => sum + val * val, 0) / window.length,
      );
      amplitude.push(rms);
    }

    // Detect suppression epochs
    let inSuppression = false;
    let suppressionStart = 0;
    let totalSuppressed = 0;

    for (let i = 0; i < amplitude.length; i++) {
      if (amplitude[i] < threshold && !inSuppression) {
        // Suppression onset
        inSuppression = true;
        suppressionStart = i;
      } else if (amplitude[i] >= threshold && inSuppression) {
        // Suppression offset
        inSuppression = false;
        const suppressionEnd = i - 1;
        const duration = (suppressionEnd - suppressionStart) * windowSize;

        if (duration >= minDuration) {
          const epochSegment = signal.slice(
            suppressionStart * windowSize,
            suppressionEnd * windowSize,
          );
          const meanAmp =
            epochSegment.reduce((sum, val) => sum + Math.abs(val), 0) /
            epochSegment.length;

          suppressionEpochs.push({
            start: (suppressionStart * windowSize) / samplingRate,
            end: (suppressionEnd * windowSize) / samplingRate,
            duration: duration / samplingRate,
            meanAmplitude: meanAmp,
          });

          totalSuppressed += duration;
        }
      }
    }

    // Handle ongoing suppression at end
    if (inSuppression) {
      const duration = (amplitude.length - suppressionStart) * windowSize;
      if (duration >= minDuration) {
        const epochSegment = signal.slice(suppressionStart * windowSize);
        const meanAmp =
          epochSegment.reduce((sum, val) => sum + Math.abs(val), 0) /
          epochSegment.length;

        suppressionEpochs.push({
          start: (suppressionStart * windowSize) / samplingRate,
          end: signal.length / samplingRate,
          duration: duration / samplingRate,
          meanAmplitude: meanAmp,
        });

        totalSuppressed += duration;
      }
    }

    const totalSuppressionTime = totalSuppressed / samplingRate;
    const totalDurationSeconds = signal.length / samplingRate;
    const suppressionRatio =
      totalDurationSeconds > 0
        ? (totalSuppressionTime / totalDurationSeconds) * 100
        : 0;

    const longestSuppression =
      suppressionEpochs.length > 0
        ? Math.max(...suppressionEpochs.map((e) => e.duration))
        : 0;

    return {
      suppressionRatio,
      suppressionEpochs,
      totalSuppressionTime,
      longestSuppression,
    };
  }

  // --------------------------------------------------------------------------
  // Burst Suppression Ratio (BSR)
  // --------------------------------------------------------------------------

  /**
   * Compute burst suppression ratio for anesthesia monitoring
   * @param signal Raw EEG signal (μV)
   * @param samplingRate Sampling rate (Hz)
   * @param params qEEG parameters
   * @returns Burst suppression analysis
   */
  computeBurstSuppressionRatio(
    signal: number[],
    samplingRate: number,
    params: Partial<QEEGParams> = {},
  ): BurstSuppressionResult {
    const fullParams = { ...QEEGMetricsService.getDefaultParams(), ...params };

    // First, detect suppressions
    const suppressionResult = this.computeSuppressionRatio(
      signal,
      samplingRate,
      params,
    );

    // Then, detect bursts (periods between suppressions with high amplitude)
    const burstThreshold = fullParams.burstThreshold;
    const minBurstDuration = Math.floor(
      fullParams.minBurstDuration * samplingRate,
    );

    const bursts: Array<{ start: number; end: number; duration: number }> = [];
    const suppressionDurations: number[] = [];
    const burstDurations: number[] = [];

    // Build timeline of burst/suppression states
    let lastSuppressionEnd = 0;

    for (const suppression of suppressionResult.suppressionEpochs) {
      const suppressionStartIdx = Math.floor(suppression.start * samplingRate);

      // Check if there's a burst between last suppression and this one
      if (suppressionStartIdx > lastSuppressionEnd + minBurstDuration) {
        const burstSegment = signal.slice(
          lastSuppressionEnd,
          suppressionStartIdx,
        );
        const meanAmp =
          burstSegment.reduce((sum, val) => sum + Math.abs(val), 0) /
          burstSegment.length;

        if (meanAmp >= burstThreshold) {
          const duration =
            (suppressionStartIdx - lastSuppressionEnd) / samplingRate;
          bursts.push({
            start: lastSuppressionEnd / samplingRate,
            end: suppressionStartIdx / samplingRate,
            duration,
          });
          burstDurations.push(duration);
        }
      }

      suppressionDurations.push(suppression.duration);
      lastSuppressionEnd = Math.floor(suppression.end * samplingRate);
    }

    // If no suppressions are detected, a continuous high-amplitude signal is one burst epoch.
    if (suppressionResult.suppressionEpochs.length === 0) {
      const meanAmplitude =
        signal.reduce((sum, val) => sum + Math.abs(val), 0) / signal.length;
      if (meanAmplitude >= burstThreshold) {
        bursts.push({
          start: 0,
          end: signal.length / samplingRate,
          duration: signal.length / samplingRate,
        });
        burstDurations.push(signal.length / samplingRate);
      }
    }

    // Compute BSR (percentage of time in suppression)
    const bsr = suppressionResult.suppressionRatio;

    // Compute burst/suppression statistics
    const burstStats = this.computeStats(burstDurations);
    const suppressionStats = this.computeStats(suppressionDurations);

    // Compute interburst interval (suppression duration)
    const interburstInterval = suppressionStats.mean;

    return {
      bsr,
      burstCount: bursts.length,
      suppressionCount: suppressionResult.suppressionEpochs.length,
      burstDuration: burstStats,
      suppressionDuration: suppressionStats,
      interburstInterval,
    };
  }

  // --------------------------------------------------------------------------
  // Asymmetry Index
  // --------------------------------------------------------------------------

  /**
   * Compute hemispheric asymmetry index for stroke detection
   * @param leftSignal Left hemisphere signal (μV)
   * @param rightSignal Right hemisphere signal (μV)
   * @param samplingRate Sampling rate (Hz)
   * @param params qEEG parameters
   * @returns Asymmetry analysis per frequency band
   */
  computeAsymmetryIndex(
    leftSignal: number[],
    rightSignal: number[],
    samplingRate: number,
    params: Partial<QEEGParams> = {},
  ): AsymmetryResult {
    const fullParams = { ...QEEGMetricsService.getDefaultParams(), ...params };

    // Compute power spectrum for both hemispheres
    const leftSpectrum = SpectralAnalysisService.computePowerSpectrum(
      leftSignal,
      samplingRate,
    );
    const rightSpectrum = SpectralAnalysisService.computePowerSpectrum(
      rightSignal,
      samplingRate,
    );

    // Extract band powers
    const leftDelta = leftSpectrum.bandPowers.find(
      (b) => b.band.name === "Delta",
    )!.absolutePower;
    const rightDelta = rightSpectrum.bandPowers.find(
      (b) => b.band.name === "Delta",
    )!.absolutePower;
    const leftTheta = leftSpectrum.bandPowers.find(
      (b) => b.band.name === "Theta",
    )!.absolutePower;
    const rightTheta = rightSpectrum.bandPowers.find(
      (b) => b.band.name === "Theta",
    )!.absolutePower;
    const leftAlpha = leftSpectrum.bandPowers.find(
      (b) => b.band.name === "Alpha",
    )!.absolutePower;
    const rightAlpha = rightSpectrum.bandPowers.find(
      (b) => b.band.name === "Alpha",
    )!.absolutePower;
    const leftBeta = leftSpectrum.bandPowers.find(
      (b) => b.band.name === "Beta",
    )!.absolutePower;
    const rightBeta = rightSpectrum.bandPowers.find(
      (b) => b.band.name === "Beta",
    )!.absolutePower;

    // Compute asymmetry for each band: (Left - Right) / (Left + Right) * 100%
    const deltaAsymmetry = this.computeBandAsymmetry(leftDelta, rightDelta);
    const thetaAsymmetry = this.computeBandAsymmetry(leftTheta, rightTheta);
    const alphaAsymmetry = this.computeBandAsymmetry(leftAlpha, rightAlpha);
    const betaAsymmetry = this.computeBandAsymmetry(leftBeta, rightBeta);

    // Compute overall asymmetry (weighted average)
    const overallAsymmetry =
      deltaAsymmetry * 0.3 +
      thetaAsymmetry * 0.2 +
      alphaAsymmetry * 0.3 +
      betaAsymmetry * 0.2;

    // Determine affected hemisphere
    let affectedHemisphere: "left" | "right" | "none" = "none";
    if (overallAsymmetry < -fullParams.asymmetrySignificanceThreshold) {
      affectedHemisphere = "right"; // Right lower power (negative asymmetry)
    } else if (overallAsymmetry > fullParams.asymmetrySignificanceThreshold) {
      affectedHemisphere = "left"; // Left lower power (positive asymmetry)
    }

    // Classify severity
    const absAsymmetry = Math.abs(overallAsymmetry);
    let severity: "mild" | "moderate" | "severe" | "none" = "none";
    if (absAsymmetry > fullParams.asymmetrySignificanceThreshold) {
      if (absAsymmetry < 20) severity = "mild";
      else if (absAsymmetry < 40) severity = "moderate";
      else severity = "severe";
    }

    return {
      deltaAsymmetry,
      thetaAsymmetry,
      alphaAsymmetry,
      betaAsymmetry,
      overallAsymmetry,
      leftHemispherePower: leftSpectrum.totalPower,
      rightHemispherePower: rightSpectrum.totalPower,
      bandAsymmetry: new Map([
        ["Delta", deltaAsymmetry],
        ["Theta", thetaAsymmetry],
        ["Alpha", alphaAsymmetry],
        ["Beta", betaAsymmetry],
      ]),
      affectedHemisphere,
      severity,
    };
  }

  private computeBandAsymmetry(leftPower: number, rightPower: number): number {
    const sum = leftPower + rightPower;
    if (sum === 0) return 0;
    return ((leftPower - rightPower) / sum) * 100;
  }

  // --------------------------------------------------------------------------
  // Coherence Analysis
  // --------------------------------------------------------------------------

  /**
   * Compute coherence between two EEG channels (functional connectivity)
   * @param signal1 First channel signal (μV)
   * @param signal2 Second channel signal (μV)
   * @param samplingRate Sampling rate (Hz)
   * @param channelNames Channel pair names
   * @param params qEEG parameters
   * @returns Coherence spectrum and band averages
   */
  computeCoherence(
    signal1: number[],
    signal2: number[],
    samplingRate: number,
    channelNames: [string, string],
    params: Partial<QEEGParams> = {},
  ): CoherenceResult {
    if (!signal1.length || !signal2.length || samplingRate <= 0) {
      throw new Error("Signals and sampling rate must be valid");
    }
    if (signal1.length !== signal2.length) {
      throw new Error("Signals must have equal length for coherence analysis");
    }

    const fullParams = { ...QEEGMetricsService.getDefaultParams(), ...params };

    // Use Welch's method with overlapping windows
    const windowSize = Math.floor(fullParams.coherenceWindow * samplingRate);
    const overlap = Math.floor(
      (windowSize * fullParams.coherenceOverlap) / 100,
    );

    // Compute cross-spectral density and power spectra
    const spectrum1 = SpectralAnalysisService.computePowerSpectrum(
      signal1,
      samplingRate,
      "hann",
    );
    const spectrum2 = SpectralAnalysisService.computePowerSpectrum(
      signal2,
      samplingRate,
      "hann",
    );

    // Approximate coherence from spectral similarity weighted by time-domain correlation.
    const coherence: number[] = [];
    const correlation = Math.abs(this.computePearsonCorrelation(signal1, signal2));

    for (let i = 0; i < spectrum1.powerSpectrum.length; i++) {
      const p1 = spectrum1.powerSpectrum[i];
      const p2 = spectrum2.powerSpectrum[i];
      const spectralSimilarity = (2 * Math.sqrt(p1 * p2)) / (p1 + p2 + 1e-10);
      const coh = Math.min(
        Math.max(0, spectralSimilarity * correlation),
        1.0,
      );
      coherence.push(coh);
    }

    // Find peak coherence
    const peakIdx = coherence.indexOf(Math.max(...coherence));
    const peakCoherence = coherence[peakIdx];
    const peakFrequency = spectrum1.frequencies[peakIdx];

    // Compute mean coherence per band
    const deltaBand = CLINICAL_BANDS.find((b) => b.name === "Delta")!;
    const thetaBand = CLINICAL_BANDS.find((b) => b.name === "Theta")!;
    const alphaBand = CLINICAL_BANDS.find((b) => b.name === "Alpha")!;
    const betaBand = CLINICAL_BANDS.find((b) => b.name === "Beta")!;
    const gammaBand = CLINICAL_BANDS.find((b) => b.name === "Gamma")!;

    const meanCoherence = {
      delta: this.meanCoherenceInBand(coherence, spectrum1.frequencies, [
        deltaBand.minHz,
        deltaBand.maxHz,
      ]),
      theta: this.meanCoherenceInBand(coherence, spectrum1.frequencies, [
        thetaBand.minHz,
        thetaBand.maxHz,
      ]),
      alpha: this.meanCoherenceInBand(coherence, spectrum1.frequencies, [
        alphaBand.minHz,
        alphaBand.maxHz,
      ]),
      beta: this.meanCoherenceInBand(coherence, spectrum1.frequencies, [
        betaBand.minHz,
        betaBand.maxHz,
      ]),
      gamma: this.meanCoherenceInBand(coherence, spectrum1.frequencies, [
        gammaBand.minHz,
        gammaBand.maxHz,
      ]),
    };

    return {
      channelPair: channelNames,
      frequency: spectrum1.frequencies,
      coherence,
      peakCoherence,
      peakFrequency,
      meanCoherence,
    };
  }

  /**
   * Compute hemispheric asymmetry between left and right hemisphere signals
   * @param leftSignal Signal from left hemisphere channels
   * @param rightSignal Signal from right hemisphere channels
   * @param samplingRate Sampling rate (Hz)
   * @param leftChannels Left hemisphere channel names
   * @param rightChannels Right hemisphere channel names
   * @param params qEEG parameters
   * @returns Hemispheric asymmetry results
   */
  computeHemisphericAsymmetry(
    leftSignal: number[],
    rightSignal: number[],
    samplingRate: number,
    leftChannels: string[],
    rightChannels: string[],
    params: Partial<QEEGParams> = {},
  ): AsymmetryResult {
    const fullParams = { ...QEEGMetricsService.getDefaultParams(), ...params };

    // Compute power spectra for both hemispheres
    const leftSpectrum = SpectralAnalysisService.computePowerSpectrum(
      leftSignal,
      samplingRate,
      "hann",
    );
    const rightSpectrum = SpectralAnalysisService.computePowerSpectrum(
      rightSignal,
      samplingRate,
      "hann",
    );

    // Calculate total power for each hemisphere
    const leftPower = leftSpectrum.totalPower;
    const rightPower = rightSpectrum.totalPower;

    // Compute band-specific asymmetry
    const bandAsymmetry = new Map<string, number>();
    let deltaAsymmetry = 0;
    let thetaAsymmetry = 0;
    let alphaAsymmetry = 0;
    let betaAsymmetry = 0;

    for (const band of CLINICAL_BANDS) {
      // Extract band power for left and right
      const leftBandPower = this.computeBandPower(
        leftSpectrum.powerSpectrum,
        leftSpectrum.frequencies,
        [band.minHz, band.maxHz],
      );
      const rightBandPower = this.computeBandPower(
        rightSpectrum.powerSpectrum,
        rightSpectrum.frequencies,
        [band.minHz, band.maxHz],
      );

      // Calculate asymmetry: (left - right) / (left + right) * 100
      const asymmetry =
        leftBandPower + rightBandPower === 0
          ? 0
          : ((leftBandPower - rightBandPower) /
              (leftBandPower + rightBandPower)) *
            100;

      bandAsymmetry.set(band.name, asymmetry);

      // Store specific band asymmetries
      if (band.name === "Delta") deltaAsymmetry = asymmetry;
      if (band.name === "Theta") thetaAsymmetry = asymmetry;
      if (band.name === "Alpha") alphaAsymmetry = asymmetry;
      if (band.name === "Beta") betaAsymmetry = asymmetry;
    }

    // Overall asymmetry using total power
    const overallAsymmetry =
      leftPower + rightPower === 0
        ? 0
        : ((leftPower - rightPower) / (leftPower + rightPower)) * 100;

    // Determine affected hemisphere
    let affectedHemisphere: "left" | "right" | "none" = "none";
    if (Math.abs(overallAsymmetry) > 10) {
      affectedHemisphere = overallAsymmetry < 0 ? "right" : "left";
    }

    // Determine severity
    let severity: "mild" | "moderate" | "severe" | "none" = "none";
    const absAsymmetry = Math.abs(overallAsymmetry);
    if (absAsymmetry > 50) severity = "severe";
    else if (absAsymmetry > 30) severity = "moderate";
    else if (absAsymmetry > 10) severity = "mild";

    return {
      deltaAsymmetry,
      thetaAsymmetry,
      alphaAsymmetry,
      betaAsymmetry,
      overallAsymmetry,
      leftHemispherePower: leftPower,
      rightHemispherePower: rightPower,
      bandAsymmetry,
      affectedHemisphere,
      severity,
    };
  }

  private computeBandPower(
    power: number[],
    frequencies: number[],
    band: [number, number],
  ): number {
    const bandPowers = power.filter(
      (_, idx) => frequencies[idx] >= band[0] && frequencies[idx] <= band[1],
    );

    if (bandPowers.length === 0) return 0;
    return bandPowers.reduce((a, b) => a + b, 0);
  }

  private meanCoherenceInBand(
    coherence: number[],
    frequencies: number[],
    band: [number, number],
  ): number {
    const bandValues = coherence.filter(
      (_, idx) => frequencies[idx] >= band[0] && frequencies[idx] <= band[1],
    );

    if (bandValues.length === 0) return 0;
    return bandValues.reduce((a, b) => a + b, 0) / bandValues.length;
  }

  private computePearsonCorrelation(signal1: number[], signal2: number[]): number {
    const n = signal1.length;
    if (!n || n !== signal2.length) return 0;

    const mean1 = signal1.reduce((sum, x) => sum + x, 0) / n;
    const mean2 = signal2.reduce((sum, x) => sum + x, 0) / n;

    let numerator = 0;
    let denom1 = 0;
    let denom2 = 0;
    for (let i = 0; i < n; i++) {
      const d1 = signal1[i] - mean1;
      const d2 = signal2[i] - mean2;
      numerator += d1 * d2;
      denom1 += d1 * d1;
      denom2 += d2 * d2;
    }

    if (denom1 === 0 || denom2 === 0) return 0;
    return numerator / Math.sqrt(denom1 * denom2);
  }

  /**
   * Compute Spectral Edge Frequency (SEF)
   * SEF95: Frequency below which 95% of power resides
   *
   * Clinical Use: Anesthesia depth, sedation monitoring
   * Lower SEF95 = deeper sedation (7-10 Hz: light, 4-7 Hz: moderate, <4 Hz: deep)
   *
   * @param signal EEG signal
   * @param samplingRate Sampling rate (Hz)
   * @param percentile Percentile threshold (default: 95)
   * @returns Spectral edge frequency (Hz)
   */
  computeSpectralEdgeFrequency(
    signal: number[],
    samplingRate: number,
    percentile: number = 95,
  ): number {
    const spectrum = SpectralAnalysisService.computePowerSpectrum(
      signal,
      samplingRate,
    );

    const totalPower = spectrum.powerSpectrum.reduce((a, b) => a + b, 0);
    const threshold = (percentile / 100) * totalPower;

    let cumulativePower = 0;
    for (let i = 0; i < spectrum.powerSpectrum.length; i++) {
      cumulativePower += spectrum.powerSpectrum[i];
      if (cumulativePower >= threshold) {
        return spectrum.frequencies[i];
      }
    }

    return spectrum.frequencies[spectrum.frequencies.length - 1];
  }

  /**
   * Compute Median Frequency
   * Frequency that divides spectrum into two equal power halves
   *
   * Clinical Use: Sedation depth, sleep depth
   * Lower median frequency = deeper sedation/sleep
   *
   * @param signal EEG signal
   * @param samplingRate Sampling rate (Hz)
   * @returns Median frequency (Hz)
   */
  computeMedianFrequency(signal: number[], samplingRate: number): number {
    return this.computeSpectralEdgeFrequency(signal, samplingRate, 50);
  }

  /**
   * Compute Spectral Entropy
   * Shannon entropy of power spectrum (normalized)
   *
   * Clinical Use: Anesthesia depth, consciousness level
   * Range: 0-1 (0 = periodic/single frequency, 1 = flat/noise)
   * Higher entropy = more consciousness/arousal
   *
   * @param signal EEG signal
   * @param samplingRate Sampling rate (Hz)
   * @returns Spectral entropy (0-1)
   */
  computeSpectralEntropy(signal: number[], samplingRate: number): number {
    const spectrum = SpectralAnalysisService.computePowerSpectrum(
      signal,
      samplingRate,
    );

    const totalPower = spectrum.powerSpectrum.reduce((a, b) => a + b, 0);
    if (totalPower === 0) return 0;

    // Normalize power spectrum to probability distribution
    const probabilities = spectrum.powerSpectrum.map((p) => p / totalPower);

    // Compute Shannon entropy: -Σ(p * log2(p))
    let entropy = 0;
    for (const p of probabilities) {
      if (p > 0) {
        entropy -= p * Math.log2(p);
      }
    }

    // Normalize to 0-1 range (divide by max possible entropy)
    const maxEntropy = Math.log2(spectrum.powerSpectrum.length);
    return maxEntropy > 0 ? entropy / maxEntropy : 0;
  }

  // --------------------------------------------------------------------------
  // Utility Functions
  // --------------------------------------------------------------------------

  private computeStats(values: number[]): {
    mean: number;
    median: number;
    std: number;
  } {
    if (values.length === 0) return { mean: 0, median: 0, std: 0 };

    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const sorted = [...values].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    const std = Math.sqrt(
      values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        values.length,
    );

    return { mean, median, std };
  }

  // --------------------------------------------------------------------------
  // Parameter Presets
  // --------------------------------------------------------------------------

  static getDefaultParams(): QEEGParams {
    return {
      // aEEG parameters
      aEEGWindowSize: 30, // 30 second windows
      aEEGHopSize: 10, // 10 second hop (time compression)

      // Suppression parameters
      suppressionThreshold: 5, // 5 μV threshold
      minSuppressionDuration: 0.5, // 0.5 second minimum

      // Burst suppression parameters
      burstThreshold: 25, // 25 μV for burst
      minBurstDuration: 0.5, // 0.5 second minimum

      // Asymmetry parameters
      asymmetrySignificanceThreshold: 10, // 10% threshold

      // Coherence parameters
      coherenceWindow: 4, // 4 second windows
      coherenceOverlap: 50, // 50% overlap
    };
  }

  static getNeonatalParams(): QEEGParams {
    const params = QEEGMetricsService.getDefaultParams();
    return {
      ...params,
      aEEGWindowSize: 60, // Longer windows for neonates
      suppressionThreshold: 10, // Higher threshold for neonates
    };
  }

  static getICUParams(): QEEGParams {
    const params = QEEGMetricsService.getDefaultParams();
    return {
      ...params,
      suppressionThreshold: 5, // Standard 5 μV
      minSuppressionDuration: 1.0, // Longer minimum for ICU
    };
  }
}

export default QEEGMetricsService;
